import { lazy } from 'react'
import type { Params, RouteObject } from 'react-router-dom'
import { Navigate, Route, Routes } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import {
  JobCatalogRootPageDocument,
  JobCatalogTreeVisibilityDocument,
  JobRoleHeaderDocument,
  NodePageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import type { MetaContext } from 'modules/core/lib/navigation/routes'
import { buildNavigation } from 'modules/core/lib/react-router'

import { JobRoleHeader } from 'modules/job_catalog/components/JobRoleHeader'
import { NodeHeader } from 'modules/job_catalog/components/NodeHeader'
import {
  NODE_UUID_TYPES,
  findNodeByUuid,
  getAllSalariesFromChildrenAttributes,
  getChildNodeType,
  getNavigationForTypeFromFunction,
  getSalariesFromAttributes,
  nodeTypeChildren,
  normalizeNodeType,
  parseTreeNodes,
} from 'modules/job_catalog/lib/nodesUtils'
import { Salary } from 'modules/job_catalog/lib/salary'
import { ItManagementPage } from 'modules/job_catalog/pages/NodePage/ItManagementPage'
import PeoplePage from 'modules/job_catalog/pages/NodePage/PeoplePage'
import SalariesPage from 'modules/job_catalog/pages/NodePage/SalariesPage'

const JobRolesPage = lazy(() => import('modules/job_catalog/pages/JobRolesPage'))
const JobCatalogPage = lazy(() => import('modules/job_catalog/pages/JobCatalogTreePage'))
const TreeNodePage = lazy(() => import('modules/job_catalog/pages/TreeNodePage'))
const UniversalImporterJobCatalogTreePage = lazy(
  () => import('modules/job_catalog/pages/UniversalImporterJobCatalogTreePage')
)
const BulkAssignCompetenciesPage = lazy(
  () => import('modules/job_catalog/pages/BulkAssingCompetenciesPage')
)
const JobCatalogTreeImportInProgressPage = lazy(
  () => import('modules/job_catalog/pages/JobCatalogTreeImportInProgressPage')
)

const NewJobRoleModalPage = lazy(() => import('modules/job_catalog/pages/NewJobRoleModalPage'))
const ImportJobsModalPage = lazy(() => import('modules/job_catalog/pages/ImportJobsModalPage'))
const ImportJobsErrorPage = lazy(() => import('modules/job_catalog/pages/ImportJobsErrorPage'))
const BulkEditPage = lazy(() => import('modules/job_catalog/pages/BulkEditPage'))
const Role = lazy(() => import('modules/job_catalog/pages/Role'))
const NodePage = lazy(() => import('modules/job_catalog/pages/NodePage'))
const NodeRedirectPage = lazy(() => import('modules/job_catalog/pages/NodePage/RedirectPage'))
const OverviewPage = lazy(() => import('modules/job_catalog/pages/NodePage/OverviewPage'))
const EmployeesPage = lazy(() => import('modules/job_catalog/pages/Role/EmployeesPage'))
const SalaryRangesPage = lazy(() => import('modules/job_catalog/pages/Role/SalaryRangesPage'))
const CompetenciesPage2 = lazy(() => import('modules/job_catalog/pages/NodePage/CompetenciesPage'))
const CareerPathPage = lazy(() => import('modules/job_catalog/pages/Role/CareerPathPage'))
const SettingsPage = lazy(() => import('modules/job_catalog/pages/Role/SettingsPage'))
const CompetenciesPage = lazy(() => import('modules/job_catalog/pages/Role/CompetenciesPage'))
const WorkingConditionsPage = lazy(
  () => import('modules/job_catalog/pages/NodePage/WorkingConditionsPage')
)
const AssignCompetenciesModalPage = lazy(
  () => import('modules/job_catalog/pages/Role/CompetenciesPage/AssignCompetenciesModalPage')
)
const CreateCompetenciesFromSuggestionsModalPage = lazy(
  () =>
    import('modules/job_catalog/pages/Role/CompetenciesPage/CreateCompetenciesFromSuggestionsModalPage')
)
const CreateCompetencyModalPage = lazy(
  () => import('modules/job_catalog/pages/Role/CompetenciesPage/CreateCompetencyModalPage')
)
const RootPage = lazy(() => import('modules/job_catalog/pages/RootPage'))

const buildNodePageMeta = ({
  watchQuery,
  params,
  session,
  nodeUuid,
  navigation,
}: {
  watchQuery: MetaContext['watchQuery']
  params: Readonly<Params<string>>
  session: MetaContext['session']
  nodeUuid: string | undefined
  navigation: Navigation<NavigationPaths>
}) => {
  const nodePageQuery = watchQuery({
    query: NodePageDocument,
    variables: {
      nodeUuid: nodeUuid ?? '',
      childType: getChildNodeType(nodeUuid),
    },
  })

  const jobCatalogTreeVisibilityQuery = watchQuery({
    query: JobCatalogTreeVisibilityDocument,
    variables: {
      companyId: session.company.id,
      featureAvailabilityIds: [Features.competencies_core, Features.it_management_v2_core],
    },
  })

  return nodePageQuery.flatMap(({ data: nodePageData }) => {
    return jobCatalogTreeVisibilityQuery.map(({ data: jobCatalogTreeVisibilityData }) => {
      if (!nodePageData.jobCatalog.treesConnection.nodes[0]) {
        return { title: '' }
      }

      const treeNodes = parseTreeNodes(nodePageData.jobCatalog.treesConnection.nodes[0])
      const foundNode = findNodeByUuid(treeNodes, nodeUuid || '')

      if (!foundNode) return { title: '' }

      const isLevel = foundNode?.type === NODE_UUID_TYPES.LEVEL
      let salaries: Salary[]
      if (isLevel) {
        const nodeAttributes = nodePageData?.jobCatalog.nodeAttributesConnection.nodes || []
        salaries = getSalariesFromAttributes(nodeAttributes)
      } else {
        salaries = getAllSalariesFromChildrenAttributes(
          nodePageData?.jobCatalog.treeNodesConnection.nodes || []
        )
      }

      const canSeeCompetencies =
        jobCatalogTreeVisibilityData?.jobCatalog._policies.jobCatalogTreeSee.can

      const featureAvailabilities =
        jobCatalogTreeVisibilityData?.companies.featureAvailabilitiesConnection.nodes ?? []
      const isItInventoryContracted = isFeatureAvailabilityEnabled(
        featureAvailabilities,
        Features.it_management_v2_core
      )

      return {
        title: foundNode.name,
        headerComponent: (
          <NodeHeader
            name={foundNode.name}
            description={foundNode.description || ''}
            type={foundNode.type}
            childrens={foundNode.children}
            salaries={salaries}
            canEditRole
            currentNode={foundNode}
          />
        ),
        subNavigation: commonSubNavigation({
          navigation,
          nodeType: foundNode.type,
          isNodeLevel: isLevel,
          params,
          canSeeCompetencies,
          isItInventoryContracted,
        }),
      }
    })
  })
}

const createNodePageRoute = (
  path: string,
  paramName: string,
  nodeType: string,
  navigation: Navigation<NavigationPaths>
): RouteObject => ({
  path,
  element: <NodePage />,
  handle: {
    resource: true,
    meta: ({ watchQuery, params, session }: MetaContext) =>
      buildNodePageMeta({
        watchQuery,
        params,
        session,
        nodeUuid: params[paramName],
        navigation,
      }),
  },
  children: [...commonNodeChildrenRoutes(navigation, nodeType)],
})

const commonNodeChildrenRoutes = (
  navigation: Navigation<NavigationPaths>,
  nodeType: string
): RouteObject[] => {
  const nav = getNavigationForTypeFromFunction(navigation, nodeType)

  return [
    { index: true, element: <NodeRedirectPage /> },
    {
      path: nav.overview.path,
      element: <OverviewPage />,
    },
    {
      path: nav.workingConditions.path,
      element: <WorkingConditionsPage />,
    },
    {
      path: nav.competencies.path,
      element: <CompetenciesPage2 />,
    },
    {
      path: nav.salaries.path,
      element: <SalariesPage />,
    },
    {
      path: nav.people.path,
      element: <PeoplePage />,
    },
    {
      path: nav.itManagement.path,
      element: <ItManagementPage />,
    },
  ]
}

const JobCatalogIndexRedirect = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const { data } = useQuery(JobCatalogRootPageDocument, {
    variables: { companyId },
  })

  const isLegacyJobCatalogFeatureEnabled = data?.features?.featuresConnection?.nodes?.[0]?.enabled

  if (isLegacyJobCatalogFeatureEnabled) {
    return <Navigate to={navigation.employees.jobCatalog.list.path} replace />
  }

  return <Navigate to={navigation.employees.jobCatalog.overview.path} replace />
}

const commonSubNavigation = ({
  navigation,
  nodeType,
  isNodeLevel,
  params,
  canSeeCompetencies,
  isItInventoryContracted,
}: {
  navigation: Navigation<NavigationPaths>
  nodeType: string
  isNodeLevel: boolean
  params: Readonly<Params<string>>
  canSeeCompetencies: boolean
  isItInventoryContracted: boolean
}) => {
  const nav = getNavigationForTypeFromFunction(navigation, nodeType)
  const navParams = {
    rootId: params.rootId || '',
    familyId: params.familyId || '',
    functionId: params.functionId || '',
    roleId: params.roleId || '',
    levelId: params.levelId || '',
  }

  return [
    {
      title: nodeTypeChildren(normalizeNodeType(nodeType)),
      href: nav.overview.fullPath(navParams),
      isVisible: !isNodeLevel,
    },
    {
      title: i18n.t('job_catalog.nodes.working_conditions.multiple'),
      href: nav.workingConditions.fullPath(navParams),
    },
    {
      title: i18n.t('job_catalog.nodes.competency.multiple'),
      href: nav.competencies.fullPath(navParams),
      isVisible: canSeeCompetencies,
    },
    {
      title: i18n.t('job_catalog.nodes.it_management.one'),
      href: nav.itManagement.fullPath(navParams),
      isVisible: true,
      variant: isItInventoryContracted ? undefined : ('upsell' as const),
    },
    {
      title: i18n.t('job_catalog.nodes.salaries.multiple'),
      href: nav.salaries.fullPath(navParams),
    },
    {
      title: i18n.t('job_catalog.nodes.people.one'),
      href: nav.people.fullPath(navParams),
    },
  ]
}

export const jobCatalogEmployeesRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.employees.jobCatalog.path,
      element: <RootPage />,
      handle: {
        meta: () => ({
          title: i18n.t('job_catalog.title'),
          description: i18n.t('job_catalog.description'),
        }),
      },
      children: [
        {
          index: true,
          element: <JobCatalogIndexRedirect />,
        },
        {
          path: navigation.employees.jobCatalog.list.path,
          element: <JobRolesPage />,
          children: [
            {
              path: navigation.employees.jobCatalog.list.add.path,
              element: <NewJobRoleModalPage />,
            },
            {
              path: navigation.employees.jobCatalog.list.import.path,
              element: <ImportJobsModalPage />,
            },
          ],
        },
        {
          path: navigation.employees.jobCatalog.overview.path,
          element: <JobCatalogPage />,
          children: [
            {
              path: navigation.employees.jobCatalog.overview.node.path,
              element: <TreeNodePage />,
            },
          ],
        },
        {
          path: navigation.employees.jobCatalog.importInProgress.path,
          element: <JobCatalogTreeImportInProgressPage />,
        },
        {
          path: navigation.employees.jobCatalog.importJobCatalogTree.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterJobCatalogTreePage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('job_catalog.import_job_catalog_tree.title'),
              headerComponent: (
                <ResourceHeader
                  title={i18n.t('job_catalog.import_job_catalog_tree.title')}
                  description={i18n.t('job_catalog.import_job_catalog_tree.description')}
                />
              ),
            }),
          },
        },
        {
          path: navigation.employees.jobCatalog.bulkAssignCompetencies.path,
          element: (
            <Routes>
              <Route path="" element={<BulkAssignCompetenciesPage />} />
            </Routes>
          ),
        },
        {
          path: navigation.employees.jobCatalog.import_errors.path,
          element: <ImportJobsErrorPage />,
          handle: {
            meta: () => ({
              title: i18n.t('job_catalog.import_errors_page.title'),
            }),
          },
        },
        {
          path: navigation.employees.jobCatalog.bulkEdit.path,
          element: <BulkEditPage />,
          handle: {
            meta: () => ({
              title: i18n.t('job_catalog.bulk_edit.assign_jobs'),
            }),
          },
        },
        {
          path: navigation.employees.jobCatalog.role.path,
          element: <Role />,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              return watchQuery({
                query: JobRoleHeaderDocument,
                variables: {
                  jobRole: Number(params.id),
                },
              }).map(({ data }) => {
                const jobRole = data.jobCatalog.role

                if (!jobRole) {
                  return { title: '' }
                }
                return {
                  headerComponent: (
                    <JobRoleHeader
                      name={jobRole.name}
                      id={jobRole.id}
                      canEditRole={data.jobCatalog.role?._policies?.editJob.can ?? false}
                    />
                  ),
                  title: jobRole.name,
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <EmployeesPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.salaryRanges.path,
              element: <SalaryRangesPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.settings.path,
              element: <SettingsPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.careerPath.path,
              element: <CareerPathPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.competencies.path,
              element: <CompetenciesPage />,
              children: [
                {
                  path: navigation.employees.jobCatalog.role.competencies.assignCompetencies.path,
                  element: <AssignCompetenciesModalPage />,
                },
                {
                  path: navigation.employees.jobCatalog.role.competencies.createCompetency.path,
                  element: <CreateCompetencyModalPage />,
                },
                {
                  path: navigation.employees.jobCatalog.role.competencies
                    .createCompetenciesFromSuggestions.path,
                  element: <CreateCompetenciesFromSuggestionsModalPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.employees.jobCatalog.root.path,
          element: <NodePage />,
          handle: {
            resource: true,
            meta: ({ watchQuery, params, session }: MetaContext) =>
              buildNodePageMeta({
                watchQuery,
                params,
                session,
                nodeUuid: params.rootId,
                navigation,
              }),
          },
          children: [
            ...commonNodeChildrenRoutes(navigation, 'root'),
            {
              ...createNodePageRoute(
                navigation.employees.jobCatalog.root.family.path,
                'familyId',
                'family',
                navigation
              ),
              children: [
                ...commonNodeChildrenRoutes(navigation, 'family'),
                {
                  ...createNodePageRoute(
                    navigation.employees.jobCatalog.root.family.function.path,
                    'functionId',
                    'function',
                    navigation
                  ),
                  children: [
                    ...commonNodeChildrenRoutes(navigation, 'function'),
                    {
                      ...createNodePageRoute('role/:roleId', 'roleId', 'level', navigation),
                      children: [
                        ...commonNodeChildrenRoutes(navigation, 'role'),
                        {
                          ...createNodePageRoute('level/:levelId', 'levelId', 'level', navigation),
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ]
})
